import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandLockup } from '../components/Brand';
import { ErrorMessage, Field } from '../components/ui';
import { IconMoon, IconSun } from '../components/icons';
import { request } from '../lib/api';
import { useTheme } from '../lib/theme';

/**
 * Öffentlicher Weg zum vergessenen Passwort.
 *
 * Eine Seite, zwei Zustände – je nachdem, ob ein Token in der Adresszeile
 * steht. Ohne Token wird der Link angefordert, mit Token das neue Passwort
 * vergeben. Getrennte Seiten wären zwei Routen für einen Vorgang, den der
 * Anwender als einen erlebt.
 *
 * Diese Seite gehört fachlich zu Review K3: Der Reset-Token wird seit dieser
 * Änderung ausschließlich per E-Mail versendet und nicht mehr in der Antwort
 * zurückgegeben. Ohne eine Seite, die den Link entgegennimmt, wäre der Weg
 * zwar sicher, aber unbenutzbar.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  return token ? <NeuesPasswort token={token} /> : <LinkAnfordern />;
}

/** Rahmen mit Markenband und Design-Umschalter – wie auf der Anmeldeseite. */
function Rahmen({ titel, hinweis, children }: { titel: string; hinweis: string; children: React.ReactNode }) {
  const { resolved, toggle } = useTheme();
  return (
    <div className="login-page">
      <main className="login-main" style={{ gridColumn: '1 / -1' }}>
        <div className="login-box">
          <div className="stack" style={{ marginBottom: 20, gap: 18 }}>
            <BrandLockup height={54} />
            <div className="row between">
              <div>
                <strong style={{ fontSize: 16 }}>{titel}</strong>
                <div className="small subtle">{hinweis}</div>
              </div>
              <button className="btn ghost icon-only" onClick={toggle} title="Design umschalten">
                {resolved === 'dark' ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </div>
          {children}
          <div className="small subtle" style={{ marginTop: 16, textAlign: 'center' }}>
            <Link to="/login">Zurück zur Anmeldung</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function LinkAnfordern() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [gesendet, setGesendet] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await request('/api/auth/forgot-password', { method: 'POST', body: { email }, skipAuth: true });
      setGesendet(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  // Die Bestätigung sagt bewusst nicht, ob die Adresse bekannt ist – sonst
  // wäre diese Seite eine bequeme Adressprüfung.
  if (gesendet) {
    return (
      <Rahmen titel="Passwort zurücksetzen" hinweis="Wir haben Ihre Anfrage entgegengenommen.">
        <div className="alert success">
          <span>
            Sofern die Adresse bekannt ist, wurde eine E-Mail mit dem weiteren Weg versendet. Der Link darin gilt
            60 Minuten. Prüfen Sie bitte auch den Spam-Ordner.
          </span>
        </div>
      </Rahmen>
    );
  }

  return (
    <Rahmen titel="Passwort zurücksetzen" hinweis="Wir senden Ihnen einen Link an Ihre hinterlegte Adresse.">
      <form className="stack" onSubmit={submit}>
        <ErrorMessage error={error} />
        <Field label="E-Mail-Adresse" required>
          <input
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@unternehmen.de"
            required
          />
        </Field>
        <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', padding: 10 }}>
          {busy ? 'Wird gesendet …' : 'Link anfordern'}
        </button>
      </form>
    </Rahmen>
  );
}

function NeuesPasswort({ token }: { token: string }) {
  const navigate = useNavigate();
  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fertig, setFertig] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (passwort !== wiederholung) {
      setError(new Error('Die beiden Eingaben stimmen nicht überein.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await request('/api/auth/reset-password', {
        method: 'POST',
        body: { token, password: passwort },
        skipAuth: true,
      });
      setFertig(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  if (fertig) {
    return (
      <Rahmen titel="Neues Passwort" hinweis="Das hat geklappt.">
        <div className="alert success">
          <span>
            Das Passwort ist gesetzt. Alle bestehenden Sitzungen wurden beendet – bitte melden Sie sich neu an.
          </span>
        </div>
      </Rahmen>
    );
  }

  return (
    <Rahmen titel="Neues Passwort" hinweis="Vergeben Sie ein neues Passwort für Ihr Konto.">
      <form className="stack" onSubmit={submit}>
        <ErrorMessage error={error} />
        <Field label="Neues Passwort" hint="Mindestens 12 Zeichen." required>
          <input
            type="password"
            value={passwort}
            autoComplete="new-password"
            autoFocus
            onChange={(event) => setPasswort(event.target.value)}
            required
          />
        </Field>
        <Field label="Wiederholung" required>
          <input
            type="password"
            value={wiederholung}
            autoComplete="new-password"
            onChange={(event) => setWiederholung(event.target.value)}
            required
          />
        </Field>
        <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', padding: 10 }}>
          {busy ? 'Wird gespeichert …' : 'Passwort speichern'}
        </button>
      </form>
    </Rahmen>
  );
}
